
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { ChevronLeft, Lock } from "lucide-react";
import { useMe } from "../hooks/useMe";
import { useCountries } from "../hooks/useCountries";
import { useCorridor } from "../hooks/useCorridor";
import { usePhoneNumber } from "../hooks/usePhoneNumber";
import { applyFieldErrors } from "../utils/fieldErrors";
import { ErrorNotice } from "../components/errors/ErrorNotice";

const SectionLabel = ({ children }) => (
  <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0a2540]/40">
    {children}
  </h2>
);

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
    {children}
  </label>
);

const PROFILE_SELECT_CLASS =
  "w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-[#0A2540] focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200";

const GENDER_OPTIONS = [
  ["female", "Female"],
  ["male", "Male"],
  ["non_binary", "Non-binary"],
  ["other", "Other"],
  ["prefer_not_to_say", "Prefer not to say"],
];

const SOURCE_OF_FUNDS_OPTIONS = [
  ["salary", "Salary or wages"],
  ["business_income", "Business"],
  ["investments", "Investments"],
];

//  ["savings", "Savings"],
//   ["inheritance", "Inheritance"],
//   ["pension", "Pension"],
//   ["family_support", "Family support"],
//   ["sale_of_assets", "Sale of assets"],
//   ["other", "Other"],

function splitFullName(fullName) {
  const parts = String(fullName ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 52,
    borderRadius: 12,
    backgroundColor: state.isDisabled ? "#F8FAFC" : "#FFFFFF",
    border: state.isFocused
      ? "1.5px solid #0A4730"
      : "1px solid #E2E8F0",
    boxShadow: state.isFocused
      ? "0 0 0 4px rgba(10, 71, 48, 0.08)"
      : "0 1px 2px rgba(0,0,0,0.03)",
    fontFamily: "inherit",
    fontSize: 14,
    paddingLeft: 4,
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    "&:hover": {
      borderColor: state.isDisabled
        ? "#E2E8F0"
        : "#0A4730",
    },
    transition: "all 0.2s ease",
  }),

  menu: (base) => ({
    ...base,
    borderRadius: 16,
    overflow: "hidden",
    zIndex: 999,
    boxShadow: "0 8px 30px rgba(10,71,48,0.12)",
    border: "1px solid #0A473015",
    fontFamily: "sans-serif",
  }),

  menuList: (base) => ({
    ...base,
    padding: 6,
    maxHeight: 220,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#0A4730"
      : state.isFocused
        ? "#F5F9F4"
        : "white",
    color: state.isSelected ? "white" : "#0A2540",
    borderRadius: 10,
    fontSize: 14,
    fontFamily: "sans-serif",
    padding: "10px 12px",
    cursor: "pointer",
    transition: "background 0.1s",
  }),

  input: (base) => ({
    ...base,
    fontFamily: "sans-serif",
    fontSize: 14,
    color: "#0A2540",
  }),

  placeholder: (base) => ({
    ...base,
    fontFamily: "sans-serif",
    fontSize: 14,
    color: "#0A254050",
  }),

  singleValue: (base, state) => ({
    ...base,
    fontFamily: "sans-serif",
    fontSize: 14,
    color: state.isDisabled
      ? "#0A254070"
      : "#0A2540",
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  dropdownIndicator: (base, state) => ({
    ...base,
    display: state.isDisabled ? "none" : "flex",
    color: state.isFocused
      ? "#0A4730"
      : "#0A254040",
    transition: "color 0.15s, transform 0.2s",
    transform: state.selectProps.menuIsOpen
      ? "rotate(180deg)"
      : "rotate(0deg)",
    paddingRight: 12,
  }),

  clearIndicator: (base) => ({
    ...base,
    color: "#0A254040",
    "&:hover": {
      color: "#0A4730",
    },
  }),

  noOptionsMessage: (base) => ({
    ...base,
    fontFamily: "sans-serif",
    fontSize: 13,
    color: "#0A254060",
    padding: "12px 8px",
  }),
};

export function EditProfilePage() {
  const navigate = useNavigate();

  const {
    profile,
    isLoadingProfile,
    updateMeAsync,
    isUpdatingMe,
    updateMeError,
  } = useMe();

  const { countries } = useCountries();

  /*
   * useCorridor is the source of truth for supported
   * BluPay countries.
   */
  const { countries: corridorCountries } =
    useCorridor();

  const countryOptions = useMemo(
    () =>
      (countries ?? []).map((c) => ({
        value: c.code,
        label: c.name,
      })),
    [countries],
  );

  const currencyOptions = useMemo(() => {
    const seen = new Set();

    return (countries ?? [])
      .map((c) => c.currency)
      .filter((currency) => {
        if (!currency || seen.has(currency)) {
          return false;
        }

        seen.add(currency);
        return true;
      })
      .sort()
      .map((currency) => ({
        value: currency,
        label: currency,
      }));
  }, [countries]);

  /*
   * =========================================================
   * PHONE COUNTRY
   * =========================================================
   *
   * This is deliberately separate from the user's account
   * country.
   *
   * Example:
   *
   * Account country: GB
   * Phone country:   KE
   *
   * The phone number itself determines which supported
   * country it belongs to when an international number is
   * entered.
   */
  const phoneCountryOptions = useMemo(
    () =>
      (corridorCountries ?? []).map(
        (country) => ({
          value: country.code,
          label: `${country.name} ${
            country.dialCode
              ? `+${String(
                  country.dialCode,
                ).replace("+", "")}`
              : ""
          }`,
          country,
        }),
      ),
    [corridorCountries],
  );

  const [
    phoneCountryCode,
    setPhoneCountryCode,
  ] = useState("");

  const {
    validate: validatePhoneNumber,
    toE164,
    toNationalNumber,
    getCountry,
  } = usePhoneNumber(phoneCountryCode);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: {
      errors,
      isDirty,
    },
  } = useForm();

  /*
   * =========================================================
   * INITIALIZE PROFILE
   * =========================================================
   */
  useEffect(() => {
    if (!profile) return;

    const {
      firstName,
      lastName,
    } = splitFullName(profile.full_name);

    const existingPhoneNumber =
      profile.phone_number ?? "";

    let detectedPhoneCountry = String(
      profile.country_code ?? "",
    )
      .trim()
      .toUpperCase();

    /*
     * If the stored number is international, let
     * libphonenumber-js determine its actual country.
     *
     * This is particularly important for NANP numbers
     * such as +1, where the country cannot be determined
     * from the dial code alone.
     */
    if (existingPhoneNumber) {
      const detected =
        getCountry(existingPhoneNumber);

      if (detected) {
        const supported = (
          corridorCountries ?? []
        ).some(
          (country) =>
            country.code === detected,
        );

        if (supported) {
          detectedPhoneCountry = detected;
        }
      }
    }

    setPhoneCountryCode(
      detectedPhoneCountry,
    );

    let localPhoneNumber =
      existingPhoneNumber;

    if (existingPhoneNumber) {
      localPhoneNumber =
        toNationalNumber(
          existingPhoneNumber,
          detectedPhoneCountry,
        ) || existingPhoneNumber;
    }

    reset({
      firstName,
      lastName,

      email: profile.email ?? "",

      whatsapp_id:
        profile.whatsapp_id ?? "",

      phone_number: localPhoneNumber,

      gender:
        profile.gender ??
        "prefer_not_to_say",

      source_of_funds:
        profile.source_of_funds ?? "",

      default_currency:
        currencyOptions.find(
          (c) =>
            c.value ===
            (profile.default_currency ??
              "GBP"),
        ) || null,

      country_code:
        countryOptions.find(
          (c) =>
            c.value ===
            (profile.country_code ??
              "GB"),
        ) || null,
    });
  }, [
    profile,
    reset,
    countryOptions,
    currencyOptions,
    corridorCountries,
    getCountry,
    toNationalNumber,
  ]);

  /*
   * =========================================================
   * PHONE COUNTRY CHANGE
   * =========================================================
   */
  const handlePhoneCountryChange = (
    option,
  ) => {
    const nextCountry =
      option?.value ?? "";

    setPhoneCountryCode(nextCountry);

    /*
     * Changing the phone country invalidates
     * the previous subscriber number.
     *
     * The user should enter a number for the
     * newly selected country.
     */
    setValue("phone_number", "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  /*
   * =========================================================
   * PHONE NUMBER CHANGE
   * =========================================================
   */
  const handlePhoneNumberChange = (
    event,
    field,
  ) => {
    const rawValue =
      event.target.value;

    const trimmed =
      rawValue.trim();

    /*
     * If the user pastes an international number,
     * automatically infer its country.
     *
     * We intentionally only attempt automatic country
     * inference when "+" is present.
     *
     * That prevents normal local typing from suddenly
     * changing the selected country.
     */
    if (trimmed.startsWith("+")) {
      const detectedCountry =
        getCountry(trimmed);

      if (detectedCountry) {
        const supported = (
          corridorCountries ?? []
        ).some(
          (country) =>
            country.code ===
            detectedCountry,
        );

        if (supported) {
          if (
            detectedCountry !==
            phoneCountryCode
          ) {
            setPhoneCountryCode(
              detectedCountry,
            );
          }

          const localNumber =
            toNationalNumber(
              trimmed,
              detectedCountry,
            );

          if (localNumber) {
            field.onChange(
              localNumber,
            );

            return;
          }
        }
      }
    }

    field.onChange(rawValue);
  };

  /*
   * =========================================================
   * SUBMIT
   * =========================================================
   */
  const onSubmit = async (data) => {
    try {
      const fullName =
        `${data.firstName.trim()} ${data.lastName.trim()}`;

      const normalizedPhoneNumber =
        toE164(
          data.phone_number,
          phoneCountryCode,
        );

      await updateMeAsync({
        full_name: fullName,

        email: data.email,

        whatsapp_id:
          data.whatsapp_id,

        phone_number:
          normalizedPhoneNumber,

        gender: data.gender,

        source_of_funds:
          data.source_of_funds,
      });

      navigate(-1);
    } catch (error) {
      applyFieldErrors(error, setError, {
        full_name: "firstName",
      });
    }
  };

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen bg-transparent pb-24 text-left">
        <div className="md:hidden bg-[#0A4730] rounded-b-[50px] h-44 animate-pulse" />

        <div className="px-5 py-6 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-14 rounded-2xl bg-gray-200 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent pb-24 text-left">
      {/* HEADER — mobile only */}
      <div className="md:hidden bg-[#0A4730] rounded-b-[50px] px-6 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              navigate(-1)
            }
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
          >
            <ChevronLeft
              className="text-white"
              size={20}
            />
          </button>

          <div>
            <p className="text-[12px] text-white opacity-80 mb-1">
              Account Settings
            </p>

            <h1 className="text-2xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              Edit Profile
            </h1>
          </div>
        </div>
      </div>

      {/* DESKTOP HEADER */}
      <div className="hidden md:flex items-center gap-3 max-w-xl mx-auto px-5 pt-10 pb-2">
        <button
          onClick={() =>
            navigate(-1)
          }
          className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30"
        >
          <ChevronLeft size={18} />
        </button>

        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
          Edit Profile
        </h1>
      </div>

      {/* FORM */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="max-w-xl mx-auto px-5 py-8 space-y-8"
      >
        {/* Personal */}
        <div className="space-y-2.5">
          <SectionLabel>
            Personal Information
          </SectionLabel>

          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FieldLabel>
                  First Name
                </FieldLabel>

                <input
                  placeholder="Jane"
                  autoComplete="given-name"
                  {...register(
                    "firstName",
                    {
                      required:
                        "First name is required",
                      validate: (
                        value,
                      ) =>
                        value.trim()
                          .length > 0 ||
                        "First name is required",
                    },
                  )}
                  className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200 mb-2"
                />

                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">
                    {
                      errors.firstName
                        .message
                    }
                  </p>
                )}
              </div>

              <div>
                <FieldLabel>
                  Last Name
                </FieldLabel>

                <input
                  placeholder="Doe"
                  autoComplete="family-name"
                  {...register(
                    "lastName",
                    {
                      required:
                        "Last name is required",
                      validate: (
                        value,
                      ) =>
                        value.trim()
                          .length > 0 ||
                        "Last name is required",
                    },
                  )}
                  className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200 mb-2"
                />

                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">
                    {
                      errors.lastName
                        .message
                    }
                  </p>
                )}
              </div>
            </div>

            <div>
              <FieldLabel>
                Email
              </FieldLabel>

              <input
                placeholder="jane@email.com"
                {...register("email", {
                  required:
                    "Email required",
                  pattern: {
                    value:
                      /\S+@\S+\.\S+/,
                    message:
                      "Invalid email",
                  },
                })}
                className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200 mb-2"
              />

              {errors.email && (
                <p className="text-red-500 text-xs mt-1">
                  {
                    errors.email
                      .message
                  }
                </p>
              )}
            </div>

            <div className="mt-5">
              <FieldLabel>
                Gender
              </FieldLabel>

              <select
                {...register(
                  "gender",
                  {
                    required:
                      "Select a gender option",
                  },
                )}
                className={
                  PROFILE_SELECT_CLASS
                }
              >
                {GENDER_OPTIONS.map(
                  ([
                    value,
                    label,
                  ]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {label}
                    </option>
                  ),
                )}
              </select>

              {errors.gender && (
                <p className="text-red-500 text-xs mt-1">
                  {
                    errors.gender
                      .message
                  }
                </p>
              )}
            </div>

            <div className="mt-5">
              <FieldLabel>
                Primary source of
                funds
              </FieldLabel>

              <select
                {...register(
                  "source_of_funds",
                  {
                    required:
                      "Select your primary source of funds",
                  },
                )}
                className={
                  PROFILE_SELECT_CLASS
                }
              >
                <option
                  value=""
                  disabled
                >
                  Select an option
                </option>

                {SOURCE_OF_FUNDS_OPTIONS.map(
                  ([
                    value,
                    label,
                  ]) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {label}
                    </option>
                  ),
                )}
              </select>

              {errors.source_of_funds && (
                <p className="text-red-500 text-xs mt-1">
                  {
                    errors
                      .source_of_funds
                      .message
                  }
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="space-y-2.5">
          <SectionLabel>
            Contact
          </SectionLabel>

          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
            {/* PHONE */}
            <div className="mb-5">
              <FieldLabel>
                Phone Number
              </FieldLabel>

              <div className="grid grid-cols-[minmax(150px,0.85fr)_minmax(0,1.4fr)] gap-2">
                <Controller
                  control={control}
                  name="phone_country_code"
                  render={() => (
                    <Select
                      value={
                        phoneCountryOptions.find(
                          (
                            option,
                          ) =>
                            option.value ===
                            phoneCountryCode,
                        ) || null
                      }
                      options={
                        phoneCountryOptions
                      }
                      styles={
                        selectStyles
                      }
                      isSearchable
                      placeholder="Country"
                      onChange={
                        handlePhoneCountryChange
                      }
                      formatOptionLabel={(
                        option,
                      ) => (
                        <span>
                          {option
                            .country
                            ?.name ||
                            option.label}{" "}
                          <span className="text-[#0A2540]/50">
                            {option
                              .country
                              ?.dialCode
                              ? `+${String(
                                  option
                                    .country
                                    .dialCode,
                                ).replace(
                                  "+",
                                  "",
                                )}`
                              : ""}
                          </span>
                        </span>
                      )}
                      noOptionsMessage={() =>
                        "No supported country found"
                      }
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="phone_number"
                  rules={{
                    required:
                      "Phone number is required",

                    validate: (
                      value,
                    ) => {
                      if (
                        !phoneCountryCode
                      ) {
                        return "Select a phone country";
                      }

                      if (
                        !value ||
                        String(
                          value,
                        ).trim() ===
                          ""
                      ) {
                        return "Phone number is required";
                      }

                      const valid =
                        validatePhoneNumber(
                          value,
                          {
                            country:
                              phoneCountryCode,
                          },
                        );

                      return (
                        valid ||
                        "Enter a valid phone number for this country"
                      );
                    },
                  }}
                  render={({
                    field,
                  }) => (
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="Phone number"
                      value={
                        field.value ??
                        ""
                      }
                      onChange={(
                        event,
                      ) =>
                        handlePhoneNumberChange(
                          event,
                          field,
                        )
                      }
                      onBlur={
                        field.onBlur
                      }
                      name={
                        field.name
                      }
                      ref={field.ref}
                      className={`w-full min-w-0 h-[52px] rounded-xl bg-white border px-4 py-3 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200 ${
                        errors.phone_number
                          ? "border-red-300"
                          : "border-slate-200"
                      }`}
                    />
                  )}
                />
              </div>

              {phoneCountryCode && (
                <p className="text-[10px] text-[#0A2540]/40 mt-2">
                  Enter the number
                  without the country
                  code. International
                  numbers are detected
                  automatically when
                  pasted.
                </p>
              )}

              {errors.phone_number && (
                <p className="text-red-500 text-xs mt-1">
                  {
                    errors
                      .phone_number
                      .message
                  }
                </p>
              )}
            </div>

            {/* WHATSAPP */}
            <div>
              <FieldLabel>
                WhatsApp ID
              </FieldLabel>

              <input
                placeholder="wa_447700900123"
                {...register(
                  "whatsapp_id",
                )}
                className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200 mb-2"
              />
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="space-y-2.5">
          <SectionLabel>
            Preferences
          </SectionLabel>

          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <FieldLabel>
                  Country
                </FieldLabel>

                <span className="flex items-center gap-1 text-[10px] text-[#0A2540]/40 mb-2">
                  <Lock
                    size={11}
                  />
                  Locked
                </span>
              </div>

              <Controller
                control={control}
                name="country_code"
                rules={{
                  required: true,
                }}
                render={({
                  field,
                }) => (
                  <Select
                    {...field}
                    options={
                      countryOptions
                    }
                    styles={
                      selectStyles
                    }
                    isDisabled
                    isSearchable
                    placeholder="Search country..."
                    noOptionsMessage={() =>
                      "No country found"
                    }
                  />
                )}
              />

              {errors.country_code && (
                <p className="text-red-500 text-xs mt-1">
                  Country is required
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <FieldLabel>
                  Default Currency
                </FieldLabel>

                <span className="flex items-center gap-1 text-[10px] text-[#0A2540]/40 mb-2">
                  <Lock
                    size={11}
                  />
                  Locked
                </span>
              </div>

              <Controller
                control={control}
                name="default_currency"
                rules={{
                  required: true,
                }}
                render={({
                  field,
                }) => (
                  <Select
                    {...field}
                    options={
                      currencyOptions
                    }
                    styles={
                      selectStyles
                    }
                    isDisabled
                    isSearchable
                    placeholder="Search currency..."
                    noOptionsMessage={() =>
                      "No currency found"
                    }
                  />
                )}
              />

              {errors.default_currency && (
                <p className="text-red-500 text-xs mt-1">
                  Currency is
                  required
                </p>
              )}
            </div>

            <p className="text-[11px] text-[#0A2540]/40 leading-relaxed">
              Country and currency are
              set at signup and
              can&apos;t be changed here
              yet - reach out to support
              if you need this updated.
            </p>
          </div>
        </div>

        <ErrorNotice
          error={updateMeError}
          fallback="Unable to update profile."
        />

        <button
          type="submit"
          disabled={
            isUpdatingMe ||
            !isDirty
          }
          className="w-full h-14 rounded-2xl bg-white text-[#0A4730] border-2 border-[#0A4730] font-bold tracking-wide hover:bg-[#0A4730] hover:text-white transition-all duration-300 active:scale-[0.98] disabled:border-slate-200 disabled:text-slate-400"
        >
          Save Changes
        </button>
      </form>
    </div>
  );
}
